import React, { useMemo, useState } from 'react';
import SortableTable from '../SortableTable';
import { Modal } from '../ui/Modal';

// data shape expected: Array<{ label: string, value: number }>
const GPSTopGraph = ({ title, data = [], className = '', height = '300px' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const items = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data
      .map(d => ({
        label: String(d.label ?? d.name ?? d.key ?? 'Non spécifié'),
        value: Number(d.value ?? d.count ?? 0)
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value); // DESC
  }, [data]);

  const total = useMemo(() => items.reduce((sum, d) => sum + d.value, 0), [items]);
  const uniqueItemsCount = useMemo(() => {
     return new Set(items.map(i => i.label)).size;
  }, [items]);

  const columns = useMemo(() => [
    {
      key: 'label',
      label: 'Nom',
      render: (row) => {
        const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
        return (
          <div className="py-1 w-full overflow-hidden" style={{ maxWidth: '220px' }}>
            <div className="font-medium text-gray-800 dark:text-gray-200 truncate" title={row.label}>{row.label}</div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded h-1.5 mt-1">
              <div
                className="h-1.5 rounded"
                style={{ width: `${pct}%`, backgroundColor: '#8B5CF6' }}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        );
      },
      className: 'w-[150px]'
    },
    {
      key: 'value',
      label: '#',
      render: (row) => <span className="tabular-nums text-gray-600 dark:text-gray-400">{row.value}</span>,
      className: 'w-16 text-right'
    },
    {
      key: 'percent',
      label: '%',
      render: (row) => {
        const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
        return <span className="tabular-nums text-gray-600 dark:text-gray-400">{pct}%</span>;
      },
      // Virtual column logic for sorting
      sortFunction: (a, b, direction) => {
        const pctA = total > 0 ? (a.value / total) : 0;
        const pctB = total > 0 ? (b.value / total) : 0;
        return direction === 'ascending' ? pctA - pctB : pctB - pctA;
      },
      rawValue: (row) => total > 0 ? Math.round((row.value / total) * 100) : 0,
      className: 'w-12 text-right'
    }
  ], [total]);

  return (
    <div className={`bg-white dark:bg-[#1e1e1e] p-2 rounded-lg border border-gray-200 dark:border-gray-900 shadow ${className} flex flex-col`} style={{ height, minHeight: height }}>
      {title && (
        <div className="mb-2 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-2.5">
              <button className="inline-flex items-center justify-center rounded-md border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-7 sm:size-8">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path d="M9 22V12h6v10" />
                </svg>
              </button>
              <span className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">{title}</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                {uniqueItemsCount}
              </span>
            </div>
            {items.length > 0 && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center justify-center rounded-md border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-7 sm:size-8"
                title="Agrandir"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400 text-center pt-10">Aucune donnée disponible.</p>
      ): (
        <>
          <div className="flex-grow overflow-hidden">
            <SortableTable
                data={items}
                columns={columns}
                emptyMessage="Aucune donnée"
                exportable
                exportFilename={`${title || 'export'}.csv`}
                disableFilters={true}
                fullHeight={true}
                hideCountLabel={true}
                initialSortBy={{ key: 'value', direction: 'descending' }}
            />
          </div>
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={title}
            maxWidth="max-w-5xl"
          >
            <div className="max-h-[70vh] overflow-y-auto">
              <SortableTable
                data={items}
                columns={columns}
                emptyMessage="Aucune donnée"
                exportable
                exportFilename={`${title || 'export'}.csv`}
                disableFilters={true}
                hideCountLabel={true}
                initialSortBy={{ key: 'value', direction: 'descending' }}
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default GPSTopGraph;


